import dayjs from 'dayjs';
import { DatePeriod } from '../enums';
import { Range, TimeLeftOptions, TimeUnit } from '../models';

/**
 * Gets the start and end date for a given month.
 *
 * @param year The year
 * @param month The month (0-11, JavaScript Date convention)
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 *
 * @example
 * const { startDate, endDate } = DateUtils.getMonthRange(2025, 10);
 * // Returns: { startDate: '2025-11-01', endDate: '2025-11-30' }
 */
export function getMonthRange(
  year: number,
  month: number
): { startDate: string; endDate: string } {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return {
    startDate: firstDay.toISOString().split('T')[0],
    endDate: lastDay.toISOString().split('T')[0]
  };
}

/**
 * Converts an input value into a JavaScript `Date` object.
 *
 * Supported input types:
 * - **string**:
 *   - If the string matches `YYYY-MM-DD`, the time `00:00:00` is appended.
 *   - If the string matches `HH:mm` or `HH:mm:ss`, today's date is used.
 *   - Otherwise, the function throws an error.
 *
 * - **number**:
 *   - Treated as a timestamp (milliseconds since Unix epoch).
 *
 * - **Date**:
 *   - Returned as-is (a new instance is created to avoid mutation).
 *
 * @param input - A value representing a date: string, number, or Date.
 * @returns A JavaScript `Date` object.
 * @throws If the string input does not match a supported format.
 */
export function parseToDate(input: string | number | Date): Date {
  // Handle Date
  if (input instanceof Date) {
    return new Date(input.getTime());
  }

  // Handle number (timestamp)
  if (typeof input === 'number') {
    return new Date(input);
  }

  // Handle string
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(input);
  const isTime = /^\d{2}:\d{2}(:\d{2})?$/.test(input);
  const isISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(input);

  if (isISO) {
    return new Date(input);
  }

  if (isDate) {
    return dayjs(`${input} 00:00:00`, 'YYYY-MM-DD HH:mm:ss').toDate();
  }

  if (isTime) {
    const today = dayjs().format('YYYY-MM-DD');
    return dayjs(`${today} ${input}`, 'YYYY-MM-DD HH:mm:ss').toDate();
  }

  throw new Error(
    'Invalid string format. Expected YYYY-MM-DD, HH:mm, HH:mm:ss, or ISO 8601.'
  );
}
export function destructureDate(
  date: string | Date | null | undefined,
  padded = false
): {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
  second: string;
} | null {
  if (!isValidDate(date) || date === null || date === undefined) {
    return null;
  }
  const d = new Date(date);

  const data = {
    day: d.getDate(),
    month: d.getMonth() + 1,
    year: d.getFullYear(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds()
  };

  return {
    day: !padded ? String(data.day) : String(data.day).padStart(2, '0'),
    month: !padded ? String(data.month) : String(data.month).padStart(2, '0'),
    year: !padded ? String(data.year) : String(data.year).padStart(2, '0'),
    hour: !padded ? String(data.hour) : String(data.hour).padStart(2, '0'),
    minute: !padded
      ? String(data.minute)
      : String(data.minute).padStart(2, '0'),
    second: !padded ? String(data.second) : String(data.second).padStart(2, '0')
  };
}

/**
 * Transform a range of date into a readable range
 * @param start : string | Date | undefined | null - Start date
 * @param end  : string | Date | undefined | null - End date
 * @param alwaysAddCurrentYear : boolean - Defines if the current year should always appear
 * @returns
 * A readable range of dates
 * @example
 * ```ts
 * datesToStringRange("2025-08-13", "2025-08-20"); // August 13 - 20, 2025
 * datesToStringRange("2025-08-13", "2025-08-20", false); // August 13 - 20
 * datesToStringRange("2025-07-13", "2025-08-20"); // July 13 - August 20, 2025
 * datesToStringRange("2025-12-13", "2026-01-11"); // December 13, 2025 - January 11, 2026
 * ```
 */
export function datesToStringRange(
  start: string | Date | undefined | null,
  end: string | Date | undefined | null,
  alwaysAddCurrentYear = true
): string {
  if (!isValidDate(start)) {
    return '';
  }
  if (!isValidDate(end)) {
    return '';
  }
  const startDate = parseToDate(start as string | Date);
  const endDate = parseToDate(end as string | Date);

  //TODO: Add a function to change the language
  const startMonth = getLabelMonth(startDate, 'short', 'en-US');
  const year = alwaysAddCurrentYear ? `, ${startDate.getFullYear()}` : '';

  if (dayjs(startDate).isSame(dayjs(endDate), 'day')) {
    return `${startMonth} ${startDate.getDate()}${year}`;
  }

  if (dayjs(startDate).isSame(dayjs(endDate), 'month')) {
    return `${startMonth} ${startDate.getDate()} - ${endDate.getDate()}${year}`;
  }

  const endMonth = getLabelMonth(endDate, 'short', 'en-US');
  if (dayjs(startDate).isSame(dayjs(endDate), 'year')) {
    return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}${year}`;
  }
  const endYear = `, ${endDate.getFullYear()}`;
  return `${startMonth} ${startDate.getDate()}${year} - ${endMonth} ${endDate.getDate()}${endYear}`;
}

/**
 * Checks whether a given string represents a valid date.
 *
 * @param testedDate - The date string to validate. It can be in any format recognized by the JavaScript `Date` object.
 * @returns `true` if the string is a valid date, otherwise `false`.
 *
 * @example
 * ```ts
 * DateUtils.isValid("2025-11-13"); // true
 * DateUtils.isValid("13/13/2025"); // false
 * ```
 */
function isValidDate(testedDate: string | Date | null | undefined): boolean {
  if (testedDate == null) return false;

  if (typeof testedDate === 'string') {
    const date = new Date(testedDate);
    return !isNaN(date.getTime());
  }

  return !isNaN(testedDate.getTime());
}

/**
 * Returns the short month name (e.g. "Jan", "Feb", "Mar") for a given date.
 * Uses `Intl.DateTimeFormat` for localization support.
 *
 * @param date - The date or date string to format.
 * @param locale - The locale to use for formatting (e.g. `"en-US"`, `"es-ES"`). Defaults to `"en-US"`.
 * @returns The short month name based on the given locale.
 *
 * @example
 * ```ts
 * DateUtils.getShortMonth("2025-08-01"); // "Aug"
 * DateUtils.getShortMonth("2025-08-01", "es-ES"); // "ago"
 * ```
 */
function getLabelMonth(
  date: Date | string,
  monthFormat: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' | undefined,
  locale: string = 'en-US'
): string {
  const d = parseToDate(date);
  if (isNaN(d.getTime())) throw new Error('Invalid date');

  return new Intl.DateTimeFormat(locale, { month: monthFormat }).format(d);
}

/**
 * Creates a new Date object based on the given date.
 * This helps ensure operations do not mutate the original date.
 *
 * @param date - The base date.
 * @returns A new Date instance with the same timestamp.
 */
export function cloneDate(date: Date): Date {
  return new Date(date.getTime());
}

/**
 * Returns a human-readable string representing how long ago the given date was.
 *
 * @param date - The date to compare against the current time. Can be a Date object or an ISO date string.
 * @returns A relative time string like "Just now", "5 minutes ago", "2 hours ago", "3 days ago", etc.
 *
 * @example
 * ```ts
 * DateUtils.getTimeAgo(new Date()); // "Just now"
 * DateUtils.getTimeAgo("2025-11-29T10:00:00Z"); // "1 day ago" (if current date is Nov 30)
 * DateUtils.getTimeAgo(new Date(Date.now() - 3600000)); // "1 hour ago"
 * ```
 */
export function getTimeAgo(date: string | Date): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - targetDate.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (months > 0) return months === 1 ? '1 month ago' : `${months} months ago`;

  if (weeks > 0) return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;

  if (days > 0) return days === 1 ? '1 day ago' : `${days} days ago`;

  if (hours > 0) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;

  if (minutes > 0)
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;

  return 'Just now';
}

/**
 * Calculates the remaining time from "now" to a target date, returning a human-readable string.
 *
 * - Uses Dayjs diff by units (years, months, weeks, days, hours, minutes, seconds).
 * - Skips any unit whose value is 0.
 * - Supports choosing the maximum and minimum unit.
 * - Supports localization by allowing custom labels and optional locale.
 *
 * @param target - Future date/time. If it is <= now, returns "0 seconds" (or minUnit if set).
 * @param options - Formatting and range options.
 * @returns A string like: "1 month 1 day 22 hours 30 seconds"
 *
 * @example
 * const now = dayjs("2026-05-01T05:00:00");
 * const target = dayjs("2026-06-03T03:00:30");
 * timeLeft(target, { now });
 * // "1 month 1 day 22 hours 30 seconds"
 */
export function timeLeft(
  target: dayjs.Dayjs | string | Date | null | undefined,
  options: TimeLeftOptions = {}
): string {
  if (!target) return '0 seconds';
  const UNITS: TimeUnit[] = [
    'year',
    'month',
    'week',
    'day',
    'hour',
    'minute',
    'second'
  ];
  //TODO: Use i18n for the labels
  const DEFAULT_EN_LABELS: Record<
    TimeUnit,
    { singular: string; plural: string }
  > = {
    year: { singular: 'year', plural: 'years' },
    month: { singular: 'month', plural: 'months' },
    week: { singular: 'week', plural: 'weeks' },
    day: { singular: 'day', plural: 'days' },
    hour: { singular: 'hour', plural: 'hours' },
    minute: { singular: 'minute', plural: 'minutes' },
    second: { singular: 'second', plural: 'seconds' }
  };

  const { maxUnit = 'year', minUnit = 'second', locale } = options;

  const baseNow = dayjs();
  const end = dayjs(target);

  // Handle invalid dates
  if (!baseNow.isValid() || !end.isValid()) {
    throw new Error('Invalid date provided to timeLeft()');
  }

  // If target is in the past or equal, return 0 of the minimum unit.
  if (!end.isAfter(baseNow)) {
    const unitLabels = { ...DEFAULT_EN_LABELS };
    const minLabel = unitLabels[minUnit];
    return `0 ${minLabel.plural}`;
  }

  // Optional: apply locale for this call (without changing global permanently)
  // Dayjs locale is global, but you can do per-instance with .locale(locale)
  let cursor = locale ? baseNow.locale(locale) : baseNow;
  const endLocalized = locale ? end.locale(locale) : end;

  const maxIndex = UNITS.indexOf(maxUnit);
  const minIndex = UNITS.indexOf(minUnit);

  if (maxIndex === -1 || minIndex === -1) {
    throw new Error('Invalid maxUnit or minUnit');
  }
  if (maxIndex > minIndex) {
    throw new Error(
      'maxUnit must be larger-or-equal to minUnit (e.g. month..second)'
    );
  }

  const unitLabels = { ...DEFAULT_EN_LABELS };

  const parts: string[] = [];

  for (let i = maxIndex; i <= minIndex; i++) {
    const unit = UNITS[i];

    // Compute whole units remaining from cursor to end
    const value = endLocalized.diff(cursor, unit, false);

    if (value > 0) {
      cursor = cursor.add(value, unit);

      const label = unitLabels[unit];
      const word = value === 1 ? label.singular : label.plural;

      parts.push(`${value} ${word}`);
    }
  }

  // If everything was 0 due to range constraints, force show minUnit = 0
  if (parts.length === 0) {
    const minLabel = unitLabels[minUnit];
    return `0 ${minLabel.plural}`;
  }

  return parts.join(' ');
}

/**
 * Get the start of the current week (Sunday)
 * @returns Date object representing Sunday of the current week
 */
export function getCurrentWeekStart(): Date {
  return dayjs().startOf('week').toDate();
}

/**
 * Get the end of the current week (Saturday)
 * @returns Date object representing Saturday of the current week
 */
export function getCurrentWeekEnd(): Date {
  return dayjs().endOf('week').toDate();
}

/**
 * Format a date to YYYY-MM-DD string (DateOnly format)
 * @param date - Date to format
 * @returns String in YYYY-MM-DD format
 */
export function formatDateOnly(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Format a date to short display format (e.g., "Sep 17")
 * @param date - Date to format
 * @returns String in "MMM D" format
 */
export function formatShortDate(date: Date | string): string {
  return dayjs(date).format('MMM D');
}

/**
 * Get the day of week name (e.g., "Wednesday")
 * @param date - Date to get day name from
 * @returns Full day name
 */
export function getDayOfWeek(date: Date | string): string {
  return dayjs(date).format('dddd');
}

export function period2DateRange(
  period: DatePeriod,
  baseDate: Date | string = new Date()
): Range<Date> {
  switch (period) {
    case 'weekly':
      return {
        start: dayjs(baseDate).startOf('week').toDate(),
        end: dayjs(baseDate).endOf('week').toDate()
      };
    case 'biweekly':
      return {
        start: dayjs(baseDate).startOf('week').toDate(),
        end: dayjs(baseDate).endOf('week').add(1, 'week').toDate()
      };
    case 'monthly':
      return {
        start: dayjs(baseDate).startOf('month').toDate(),
        end: dayjs(baseDate).endOf('month').toDate()
      };
    case 'yeartodate':
      return {
        start: dayjs(baseDate).startOf('year').toDate(),
        end: dayjs(baseDate).endOf('year').toDate()
      };
    default:
      return {
        start: dayjs(baseDate).startOf('week').toDate(),
        end: dayjs(baseDate).endOf('week').toDate()
      };
  }
}

/**
 * Calculates the difference in days between two dates or today and an end date.
 *
 * If the start date is today or in the future:
 * Returns the difference between start date and end date (e.g., "8 days", "1 day").
 *
 * If the start date is in the past:
 * Returns the difference between today and the end date (e.g., "3 days left", "1 day left").
 *
 * Special cases:
 * - If the result is 0 or negative, returns "0 days".
 *
 * @param startDate The start date of the period.
 * @param endDate The end date of the period.
 * @returns A formatted string representing the day difference.
 */
export function mapRemainingDays(
  startDate: string | Date | undefined | null,
  endDate: string | Date | undefined | null
): string {
  if (!startDate || !endDate) {
    return '0 days';
  }

  const today = dayjs().startOf('day');
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  if (start.isBefore(today)) {
    const diff = end.diff(today, 'day');
    if (diff <= 0) {
      return '0 days';
    }
    return `${diff} day${diff === 1 ? '' : 's'} left`;
  }

  const diff = end.diff(start, 'day');
  if (diff <= 0) {
    return '0 days';
  }
  return `${diff} day${diff === 1 ? '' : 's'}`;
}
